import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  Menu,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState, useEffect, useContext, useRef } from "react";
import ltcpRoutes from "../constants/ltcpRoutes";
import { useNavigate } from "react-router-dom";
import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";
import TuneIcon from "@mui/icons-material/Tune";
import LaunchIcon from "@mui/icons-material/Launch";
import indiaGeoJSON from "./INDIA_STATES.geo.json";
import DemandCapacityGraph from "../components/DemandCapacityGraph";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { LTCPContext } from "../LTCPContext";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  getAlertCount,
  getCapacityDefault,
  getCuData,
  getCuFilters,
  getDashboardCuData,
  getDashboardCuNestedData,
  getDashboardDemanMap,
  getClusterData,
} from "LTCP Module/api/ltcpBackend";
import {
  BRANCH_CODE_TO_NAME,
  CLUSTER_CODE_TO_NAME,
  CLUSTER_BRANCH_MAPPING
} from "LTCP Module/constants/mapping";
import MultiSearchFilter from "LTCP Module/components/MultiSearchFilter";
import ExpandIcon from "LTCP Module/components/ExpandIcon";
import CollapseIcon from "LTCP Module/components/CollapseIcon";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import {
  branchFilterValueConvert,
  monthFilterValueConvert,
} from "utils/filterValueConverter";
import { useMsal } from "@azure/msal-react";
import { roundOffNumber } from "utils/roundOff";
import PillBox from "../components/PillBox"; 

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const BRANCH_STATE_MAPPING = {
  MAHARASHTRA: "B001",
  GUJARAT: "B001",
  "ARUNACHAL PRADESH": "B002",
  ASSAM: "B002",
  MANIPUR: "B002",
  MEGHALAYA: "B002",
  MIZORAM: "B002",
  NAGALAND: "B002",
  TRIPURA: "B002",
  SIKKIM: "B002",
  JHARKHAND: "B002",
  ODISHA: "B002",
  "WEST BENGAL": "B002",
  DELHI: "B003",
  PUNJAB: "B003",
  HARYANA: "B003",
  RAJASTHAN: "B003",
  TELANGANA: "B004",
  "ANDHRA PRADESH": "B004",
  KARNATAKA: "B004",
  KERALA: "B004",
  "TAMIL NADU": "B004",
  BIHAR: "B010",
  "MADHYA PRADESH": "B010",
  CHHATTISGARH: "B010",
  "UTTAR PRADESH": "B010",
  "JAMMU & KASHMIR": "B003",
  "HIMACHAL PRADESH": "B003",
  UTTARAKHAND: "B003",
  LADAKH: "B003",
  GOA: "B001",
};

const CLUSTER_STATE_MAPPING = {
  "TAMIL NADU": "CL01",
  KARNATAKA: "CL02",
  TELANGANA: "CL03",
  "ANDHRA PRADESH": "CL03",
  GUJARAT: "CL04",
  MAHARASHTRA: "CL05 + CL06",
  DELHI: "CL07",
  PUNJAB: "CL08",
  HARYANA: "CL08",
  "WEST BENGAL": "CL09",
  JHARKHAND: "CL10",
  ODISHA: "CL10",
  "ARUNACHAL PRADESH": "CL11",
  ASSAM: "CL11",
  MANIPUR: "CL11",
  MEGHALAYA: "CL11",
  MIZORAM: "CL11",
  NAGALAND: "CL11",
  TRIPURA: "CL11",
  SIKKIM: "CL11",
  "UTTAR PRADESH": "CL12",
  BIHAR: "CL13",
  "MADHYA PRADESH": "CL14",
  CHHATTISGARH: "CL14",
  KERALA: "CL15",
  RAJASTHAN: "CL16",
  "JAMMU & KASHMIR": "CL08",
  "HIMACHAL PRADESH": "CL08",
  UTTARAKHAND: "CL08",
  LADAKH: "CL08",
  GOA: "CL06",
};

// Use the common round off function with error handling feature
// to avoid error for the values where null are expected.
function roundNumber(val) {
  try {
    return roundOffNumber(val);
  } catch {
    return val;
  }
}

// This is a stateless component used for showing the kpi data on the overview
// page. Based on the given two colors it will create a card with linear gradient
// background color and will hightlight the given value with the label and
// the given unit. This will also work as a navigation button and will redirect
// to the given link.
export  function OverviewButton({ label, value, unit, onClick, colorFrom, colorTo, flag = false }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "space-between",
        width: flag ? "250px" : "120px",
        padding: "6px 16px",
        color: "white",
        borderRadius: "3px",
        background: `linear-gradient(to right, ${colorFrom}, ${colorTo})`,
      }}
    >
      <Typography fontSize={14}>{label}</Typography>
      <Stack gap={1} direction="row" alignItems="end">
        <Typography fontSize={20} fontWeight="bold">
          {value}
        </Typography>
        <Typography fontSize={12}>{unit}</Typography>
      </Stack>
      <LaunchIcon
        sx={{ position: "absolute", top: 5, right: 5, fontSize: 10 }}
      />
    </ButtonBase>
  );
}

// This row component contain the top level and nested table rows for
// the overview page demand-capacity-cu table for all the granularity:
// national, branch and factory.
function NestedTableRow({
  data,
  level,
  bgState,
  rowIndex,
  unitToggle,
  subcategoryState,
  branchState,
  brandState,
  formatState,
  factoryState,
  yearState,
  quarterState,
  monthState,
  timeGranularity,
  tableLevel,
  tableSortCol,
  tableSortOrd,
  load_18_months,
  threshold
}) {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const activeAccount = accounts[0];
  const userEmail = activeAccount.username;

  const { setLoading, demandFile, runtime, runtype } = useContext(LTCPContext);
  const [nestedData, setNestedData] = useState([]);

  function toggleClass() {
    let elmt = document.getElementById("dashboard-table-btn-" + rowIndex);
    if (elmt) {
      if (elmt.classList.contains("ltcp-row-expand"))
        elmt.classList.remove("ltcp-row-expand");
      else elmt.classList.add("ltcp-row-expand");
    }
  }

  async function fetchData() {
    setLoading(true);

    let payload = {};

    if (level === "national") {
      payload = {
        demandFile,
        bgState,
        subcategoryState,
        branchState: branchFilterValueConvert(branchState),
        brandState: data.brand,
        formatState,
        basepackState: "All",
        factoryState,
        yearState,
        quarterState,
        monthState,
        timeGranularity,
        tableGranularity: tableLevel,
        runtime,
        runtype,
        month_18: load_18_months,
      };
    } else if (level === "branch") {
      payload = {
        demandFile,
        bgState,
        subcategoryState,
        branchState: data.branch,
        brandState,
        formatState,
        basepackState: "All",
        factoryState,
        yearState,
        quarterState,
        monthState,
        timeGranularity,
        tableGranularity: tableLevel,
        runtime,
        runtype,
        month_18: load_18_months,
      };
    } else if (level === "factory") {
      payload = {
        demandFile,
        bgState,
        subcategoryState,
        branchState: branchFilterValueConvert(branchState),
        brandState,
        formatState,
        basepackState: "All",
        factoryState: data.factory,
        yearState,
        quarterState,
        monthState,
        timeGranularity,
        tableGranularity: tableLevel,
        runtime,
        runtype,
        month_18: load_18_months,
      };
    }

    const resp = await getDashboardCuNestedData(userEmail, payload);

    toggleClass();
    setNestedData(resp);
    setLoading(false);
  }

  function collapseRows() {
    setNestedData([]);
    toggleClass();
  }

  return (
    <>
      <TableRow>
        <TableCell sx={{ borderBottom: "1px solid #ccc" }}>
          <ButtonBase
            id={"dashboard-table-btn-" + rowIndex}
            onClick={() =>
              nestedData.length === 0 ? fetchData() : collapseRows()
            }
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: 12,
              gap: 2,
              textWrap: "nowrap",
            }}
          >
            {nestedData.length > 0 ? <CollapseIcon /> : <ExpandIcon />}
            {level === "national" && data.brand}
            {level === "branch" && data.branch}
            {level === "factory" && data.factory}
          </ButtonBase>
        </TableCell>
        {level !== "national" && (
          <TableCell
            sx={{ borderBottom: "1px solid #ccc", textWrap: "nowrap" }}
          >
            All
          </TableCell>
        )}
        <TableCell sx={{ borderBottom: "1px solid #ccc", textWrap: "nowrap" }}>
          All
        </TableCell>
        <TableCell
          sx={{
            textAlign: "center",
            borderBottom: "1px solid #ccc",
            textWrap: "nowrap",
            boxSizing: "border-box",
            pr: 4,
          }}
        >
          {unitToggle === "MU"
            ? roundNumber(data.demand_mu)
            : roundNumber(data.demand)}
        </TableCell>
        <TableCell
          sx={{
            textAlign: "center",
            borderBottom: "1px solid #ccc",
            textWrap: "nowrap",
            boxSizing: "border-box",
            pr: 4,
          }}
        >
          {unitToggle === "MU"
            ? roundNumber(data.cap_mu)
            : roundNumber(data.pack_cap_tpm_new)}
        </TableCell>
        <TableCell
          sx={{
            textAlign: "center",
            borderBottom: "1px solid #ccc",
            textWrap: "nowrap",
            boxSizing: "border-box",
            pr: 4,
          }}
        >
       <PillBox value={`${roundNumber(data.cu_const)}`}  bgColor = { `${runtype.toLowerCase() !== "schedule" && (data.cu_const >= threshold || data.cu_const === "No Capacity") ?"#ff9998":"White"}`}/>
        </TableCell>
      </TableRow>
      {(tableSortOrd
        ? nestedData.sort((a, b) =>
            typeof a[tableSortCol] === "string"
              ? a[tableSortCol].localeCompare(b[tableSortCol])
              : a[tableSortCol] - b[tableSortCol]
          )
        : nestedData.sort((a, b) =>
            typeof a[tableSortCol] === "string"
              ? b[tableSortCol].localeCompare(a[tableSortCol])
              : b[tableSortCol] - a[tableSortCol]
          )
      ).map((item, index) => (
        <TableRow key={index + "_child"}>
          <TableCell
            sx={{ background: "#b4bfff", textWrap: "nowrap" }}
          ></TableCell>
          {level !== "national" && (
            <TableCell sx={{ background: "#b4bfff", textWrap: "nowrap" }}>
              {item.brand}
            </TableCell>
          )}
          <TableCell
            sx={{
              background: "#b4bfff",
              textWrap: "nowrap",
              textDecoration: "underline",
              color: "dodgerblue",
              cursor: "pointer",
            }}
            onClick={() =>
              navigate(ltcpRoutes.ALERTS, {
                state:
                  level === "national"
                    ? { format: item.format, tab: level }
                    : level === "branch"
                    ? {
                        format: item.format,
                        tab: level,
                        branch: "Branch:" + data.branch,
                      }
                    : {
                        format: item.format,
                        tab: level,
                        factory: "Factory:" + data.code,
                      },
              })
            }
          >
            {item.format}
          </TableCell>
          <TableCell
            sx={{
              background: "#b4bfff",
              textWrap: "nowrap",
              textAlign: "center",
              pr: 4,
            }}
          >
            {unitToggle === "MU"
              ? roundNumber(item.demand_mu)
              : roundNumber(item.demand)}
          </TableCell>
          <TableCell
            sx={{
              background: "#b4bfff",
              textWrap: "nowrap",
              textAlign: "center",
              pr: 4,
            }}
          >
            {unitToggle === "MU"
              ? roundNumber(item.cap_mu)
              : roundNumber(item.pack_cap_tpm_new)}
          </TableCell>
          <TableCell
            sx={{
              // background: "#FFFFF",
              textWrap: "nowrap",
              textAlign: "center",
              pr: 4,
            }}
          >
            <PillBox value={`${getCUValue(unitToggle === "MU" 
              ? roundNumber(item.demand_mu) 
              : roundNumber(item.demand),unitToggle === "MU"
              ? roundNumber(item.cap_mu)
              : roundNumber(item.pack_cap_tpm_new),
              item.cu_const)}`}  bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.cu_const >= threshold || getCUValue(unitToggle === "MU" 
              ? roundNumber(item.demand_mu) 
              : roundNumber(item.demand),unitToggle === "MU"
              ? roundNumber(item.cap_mu)
              : roundNumber(item.pack_cap_tpm_new),
              item.cu_const) === "No Capacity") ?"#ff9998":"White"}`} />
          </TableCell>
    
        </TableRow>
      ))}
    </>
  );
}
function getCUValue(demand, capacity, cu) {
  demand = roundOffNumber(demand);
  capacity = roundOffNumber(capacity);

  if (demand === 0 && capacity === 0) {
    return "0%";
  } else if (demand > 0 && capacity === 0) {
    return "No Capacity";
  } else {
    return roundOffNumber(cu) + "%";
  }
}

// Helper function to get the remaining months of the current year from
// the current month as mmm format in string data type.
function getRemainingMonths() {
  const currentMonthIndex = new Date().getMonth();

  return monthNames.slice(currentMonthIndex).join(",");
}

// This is the overview page component with the filters, india map graph
// demand capacity graph and cu table. This page also contain unit toggle
// related logics
function DashboardPage() {
  const navigate = useNavigate();
  const tableRef = useRef();
  const { accounts } = useMsal();
  const activeAccount = accounts[0];
  const userEmail = activeAccount.username;

  const {
    cuFilters,
    setLoading,
    demandFile,
    runtime,
    runtype,
    setCapacityDefault,
    thresholdLevel,
    productType,
    commonSelectedFilters,
    setCommonSelectedFilters,
    dashboardSelectedFilters,
    setDashboardSelectedFilters,
  } = useContext(LTCPContext);

  const [currentFilters, setCurrentFilters] = useState(cuFilters);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [unitToggle, setUnitToggle] = useState("Tonnes");
  const [mapValueToggle, setMapValueToggle] = useState("Demand");
  const [filterDropdown, setFilterDropdown] = useState(null);
  const {
    mapGranularity,
    tableLevel,
  } = dashboardSelectedFilters;

  const {
    bgState,
    subcategoryState,
    branchState,
    brandState,
    formatState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    appliedFilters,
    load_ym,
  } = commonSelectedFilters;
  const timeGranularity = commonSelectedFilters.timeGranularity || "MoM";
  const setCommonSelectedFilter = (key, value) =>
    setCommonSelectedFilters((prev) => ({ ...prev, [key]: value }));
  const setDashboardSelectedFilter = (key, value) =>
    setDashboardSelectedFilters((prev) => ({ ...prev, [key]: value }));
  const setMapGranularity = (value) =>
    setDashboardSelectedFilter("mapGranularity", value);
  const setBgState = (value) => setCommonSelectedFilter("bgState", value);
  const setSubcategoryState = (value) =>
    setCommonSelectedFilter("subcategoryState", value);
  const setBranchState = (value) =>
    setCommonSelectedFilter("branchState", value);
  const setBrandState = (value) =>
    setCommonSelectedFilter("brandState", value);
  const setFormatState = (value) =>
    setCommonSelectedFilter("formatState", value);
  const setFactoryState = (value) =>
    setCommonSelectedFilter("factoryState", value);
  const setYearState = (value) => setCommonSelectedFilter("yearState", value);
  const setQuarterState = (value) =>
    setCommonSelectedFilter("quarterState", value);
  const setMonthState = (value) =>
    setCommonSelectedFilter("monthState", value);
  const setTableLevel = (value) =>
    setDashboardSelectedFilter("tableLevel", value);
  const setTimeGranularity = (value) =>
    setCommonSelectedFilter("timeGranularity", value);
  const setAppliedFilters = (value) =>
    setCommonSelectedFilter("appliedFilters", value);
  const setLoad18Months = (value) =>
    setCommonSelectedFilter("load_ym", value);
  const [tableSortCol, setTableSortCol] = useState("cu_const");
  const [tableSortOrd, setTableSortOrd] = useState(false);
  const [rowToggleState, setRowToggleState] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [maharashtraData, setMaharashtraData] = useState({
    code: "",
    name: "",
    demand: 0,
    demand_mu: 0,
    growth: 0,
  });
  const [cuData, setCuData] = useState({
    date: [],
    demand: [],
    demand_mu: [],
    pack_cap_tpm: [],
    pack_cap_tpm_new: [],
    cap_mu: [],
    cu_const: [],
    cu_unconst: [],
  });
  const [tableData, setTableData] = useState({
    kpi: {
      demand: 0,
      demand_mu: 0,
      cu_const: 0,
      cu_unconst: 0,
      cap_const: 0,
      cap_unconst: 0,
      cap_mu: 0,
    },
    location: [],
    data: [],
  });
  const [heatmapData, setHeatmapData] = useState({
    demand: [],
    growth: [],
  });
  const [branchMasterData,setBranchMasterData] = useState([]);
  async function fetchCapacityDefault() {
    const data = await getCapacityDefault({
      demand_file: demandFile,
      runtype,
      runtime,
    });

    setCapacityDefault(data);
  }

  useEffect(() => {
    fetchCapacityDefault();
  }, []);

  function toggleAllRows() {
    tableData.data.forEach((_, i) => {
      let elmt = document.getElementById("dashboard-table-btn-" + i);

      if (elmt) {
        if (rowToggleState) {
          if (elmt.classList.contains("ltcp-row-expand")) elmt.click();
        } else {
          if (!elmt.classList.contains("ltcp-row-expand")) elmt.click();
        }
      }
    });

    setRowToggleState(!rowToggleState);
  }

  function clearFilterHandler() {
    setBgState("All");
    setSubcategoryState("All");
    setBranchState("All");
    setBrandState("All");
    setFormatState("All");
    setFactoryState("All");

    setFilterDropdown(null);
    setAppliedFilters([]);
  }

  const exportToExcel = () => {
    const table = tableRef.current;
    const worksheet = XLSX.utils.table_to_sheet(table); // Convert table to sheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(dataBlob, "dashboard-data.xlsx");
  };

  async function fetchDashboardData() {
    setLoading(true);
    const resp = await Promise.all([
      getDashboardCuData(userEmail, {
        demandFile,
        bgState,
        subcategoryState,
        branchState: branchFilterValueConvert(branchState),
        brandState,
        formatState,
        basepackState: "All",
        factoryState,
        yearState,
        quarterState,
        monthState: monthFilterValueConvert(monthState),
        timeGranularity,
        tableGranularity: tableLevel,
        runtime,
        runtype,
        month_18: load_ym,
      }),
      getCuData(userEmail, {
        demandFile,
        bgState,
        subcategoryState,
        branchState: branchFilterValueConvert(branchState),
        brandState,
        formatState,
        basepackState: "All",
        factoryState,
        yearState,
        quarterState,
        monthState: monthFilterValueConvert(monthState),
        timeGranularity,
        runtime,
        runtype,
        month_18: load_ym,
      }),
      getDashboardDemanMap(userEmail, {
        demandFile,
        bgState,
        subcategoryState,
        branchState: branchFilterValueConvert(branchState),
        brandState,
        formatState,
        basepackState: "All",
        factoryState,
        yearState,
        quarterState,
        monthState: monthFilterValueConvert(monthState),
        timeGranularity,
        mapGranularity,
        runtime,
        runtype,
        prod_type: productType,
        month_18: load_ym,
      }),
      getCuFilters(userEmail, {
        demandFile,
        bgState,
        subcategoryState,
        branchState: branchFilterValueConvert(branchState),
        brandState,
        formatState,
        basepackState: "All",
        factoryState,
        runtime,
        runtype,
      }),
      getAlertCount({
        demand_file: demandFile,
        bg: bgState,
        subcategory: subcategoryState,
        branch: branchFilterValueConvert(branchState),
        factory: factoryState,
        format: formatState,
        runtime,
        runtype,
        user_email: userEmail,
      }),
    ]);

    let mapData = resp[2];

    if (mapGranularity === "Cluster") {
      const dupDemandData = resp[2].demand.filter(
        (item) => item.name === "MAHARASHTRA"
      );

      const dupGrowthData = resp[2].growth.filter(
        (item) => item.name === "MAHARASHTRA"
      );

      if (dupDemandData.length === 1) {
        let demand_value = roundOffNumber(dupDemandData[0].demand / 1000);
        if (demand_value === 0) {
          demand_value = (dupDemandData[0].demand / 1000).toFixed(2);
        }

        let demand_mu_value = roundOffNumber(dupDemandData[0].demand_mu);
        if (demand_mu_value === 0) {
          demand_mu_value = dupDemandData[0].demand_mu.toFixed(2);
        }

        setMaharashtraData({
          code: "CL05 + CL06",
          name: "MUMBAI-PUNE + GREATER MAHARASHTRA",
          demand: demand_value,
          demand_mu: demand_mu_value,
          growth: dupGrowthData[0].growth,
        });
      } else if (dupDemandData.length === 2) {
        let demand_value_1 = roundOffNumber(dupDemandData[0].demand / 1000);
        if (demand_value_1 === 0) {
          demand_value_1 = (dupDemandData[0].demand / 1000).toFixed(2);
        }

        let demand_mu_value_1 = roundOffNumber(dupDemandData[0].demand_mu);
        if (demand_mu_value_1 === 0) {
          demand_mu_value_1 = dupDemandData[0].demand_mu.toFixed(2);
        }

        let demand_value_2 = roundOffNumber(dupDemandData[1].demand / 1000);
        if (demand_value_2 === 0) {
          demand_value_2 = (dupDemandData[1].demand / 1000).toFixed(2);
        }

        let demand_mu_value_2 = roundOffNumber(dupDemandData[1].demand_mu);
        if (demand_mu_value_2 === 0) {
          demand_mu_value_2 = dupDemandData[1].demand_mu.toFixed(2);
        }

        setMaharashtraData({
          code: "CL05 + CL06",
          name: "MUMBAI-PUNE + GREATER MAHARASHTRA",
          demand: demand_value_1 + " + " + demand_value_2,
          demand_mu: demand_mu_value_1 + " + " + demand_mu_value_2,
          growth: dupGrowthData[0].growth + "% + " + dupGrowthData[1].growth,
        });
      }

      const maxDemandData = dupDemandData.reduce(
        (max, obj) => (obj.demand > max.demand ? obj : max),
        dupDemandData[0]
      );

      const maxGrowthData = dupGrowthData.reduce(
        (max, obj) => (obj.growth > max.growth ? obj : max),
        dupGrowthData[0]
      );

      let unqDemandData = resp[2].demand.filter(
        (item) => item.name !== "MAHARASHTRA"
      );

      unqDemandData.push(maxDemandData);

      let unqGrowthData = resp[2].growth.filter(
        (item) => item.name !== "MAHARASHTRA"
      );

      unqGrowthData.push(maxGrowthData);

      mapData = { demand: unqDemandData, growth: unqGrowthData };
    }

    if (resp[3] === 401) return navigate(ltcpRoutes.HOME);

    const filters = resp[3];

    appliedFilters.forEach((filter) => {
      filters[filter] = currentFilters[filter];
    });

    setAlertCount(resp[4].count);
    setTableData(resp[0]);
    setCuData(resp[1]);
    setHeatmapData(mapData);
    setCurrentFilters(filters);
    setLoading(false);
  }

  useEffect(() => {
    setRowToggleState(false);
  }, [tableLevel, timeGranularity]);

  useEffect(() => {
    if (cuFilters.bg.length === 0) return navigate(ltcpRoutes.LANDING);

    fetchDashboardData();
  }, [
    bgState,
    subcategoryState,
    branchState,
    brandState,
    formatState,
    factoryState,
    yearState,
    quarterState,
    monthState,
    timeGranularity,
    mapGranularity,
    tableLevel,
    load_ym,
  ]);

  useEffect(() => {
    if (!echarts.getMap("India")) {
      echarts.registerMap("India", indiaGeoJSON, {
        Ladakh: { left: 0, top: 0, width: 32 },
      });
      setMapLoaded(true);
    } else {
      setMapLoaded(true);
    }
  }, []);
useEffect(() => {
  async function fetchBranchClusterData(email) {
    try {
      const clusterData = await getClusterData(email);
      setBranchMasterData(clusterData);
    } 
    catch (error) {
      if (error?.response?.status === 401) {
        navigate(ltcpRoutes.HOME);
      }
    }
  }
  if (userEmail) {
    fetchBranchClusterData(userEmail);
  }
}, [userEmail, navigate]);
  // Function to handle circle click
  const handleCircleClick = (params) => {
    if (params.seriesType === "scatter") {
      setPopupData({
        name: params.name,
        info: params.data.info,
        details: params.data.details,
        coordinates: params.value,
      });
    }
  };
const getClusterDetails = (clusterName, primaryList, secondaryList) => {
  const allLists = [...primaryList, ...secondaryList];
  const clusterItem = allLists.find(item => item.clustername === clusterName);
  if (!clusterItem) return null;
  const matchInPrimary = primaryList.find(item => item.clustercode === clusterItem.clustercode);
  if (matchInPrimary) return matchInPrimary;
  return secondaryList.find(item => item.clustercode === clusterItem.clustercode) || null;
};
const getBranchDetails = (clusterName, primaryList, secondaryList) =>{
   const allLists = [...primaryList, ...secondaryList];
   return allLists.find(item => item.clustername === clusterName) || null;
}

  useEffect(() => {
    window.handleFactoryNavigation = (factory) => {
      navigate(ltcpRoutes.ALERTS, { state: { tab: "factory", factory } });
    };

    return () => {
      delete window.handleFactoryNavigation;
    };
  }, []);

  const option = {
    tooltip: {
      trigger: "item",
      enterable: true,
      borderColor: "white",
      borderWidth: 0,
      position: function (point, params, dom, rect, size) {
        const [mouseX, mouseY] = point;
        const { contentSize, viewSize } = size;

        let posX = mouseX + 5;
        let posY = mouseY;

        if (mouseY + contentSize[1] > viewSize[1]) {
          posY = mouseY - contentSize[1];
        }

        return [posX, posY];
      },
      formatter: (params) => {
        if (params.seriesType === "scatter") {
          const { name, value, data } = params;

          if (data.count === 1) {
            let color = "black";

            let threshold_value =
              productType !== "All"
                ? thresholdLevel[productType]
                : thresholdLevel[subcategoryState];

            if (!threshold_value) {
              threshold_value = 90;
            }

            if (data.cu >= threshold_value) {
              color = "red";
            } else {
              color = "black";
            }

            let demand_val = unitToggle === "MU" ? data.demand_mu : data.demand;
            let capacity_val =
              unitToggle === "MU" ? data.capacity_mu : data.capacity;
            let unit = unitToggle === "MU" ? "MU" : "KT";

            let v1 = roundOffNumber(demand_val);
            let v2 = roundOffNumber(capacity_val);

            if (v1 > 0) {
              demand_val = v1;
            } else {
              demand_val = demand_val.toFixed(2);
            }

            if (v2 > 0) {
              capacity_val = v2;
            } else {
              capacity_val = capacity_val.toFixed(2);
            }

            return `
              <div style='font-size: 12px; color: black; display: flex; flex-direction: column;'>
                <div style='display: flex; align-items: center;'><p style='width: 80px'>Factory</p>: ${name}</div>
                <div style='display: flex; align-items: center;'><p style='width: 80px'>Demand</p>: ${demand_val} ${unit}</div>
                <div style='display: flex; align-items: center;'><p style='width: 80px'>Capacity</p>: ${capacity_val} ${unit}</div>
                <div style='display: flex; align-items: center; color: ${color}'><p style='width: 80px'>CU%</p>: ${data.cu}%</div>
                <button onclick="handleFactoryNavigation('Factory:${data.code}')" style='background: none; padding: 0; width: 100%; text-align: right; color: dodgerblue; text-decoration: underline;'>View more</button>
              </div>
            `;
          } else {
            let multiple_data = tableData.location.filter((record) => {
              return record.pin === data.pin;
            });

            let tooltip_html = "";

            multiple_data.forEach((fact_data) => {
              let color = "black";

              let threshold_value =
                productType !== "All"
                  ? thresholdLevel[productType]
                  : thresholdLevel[subcategoryState];

              if (!threshold_value) {
                threshold_value = 90;
              }

              if (fact_data.cu >= threshold_value) {
                color = "red";
              } else {
                color = "black";
              }

              let demand_val =
                unitToggle === "MU" ? fact_data.demand_mu : fact_data.demand;
              let capacity_val =
                unitToggle === "MU"
                  ? fact_data.capacity_mu
                  : fact_data.capacity;
              let unit = unitToggle === "MU" ? "MU" : "KT";

              let v1 = roundOffNumber(demand_val);
              let v2 = roundOffNumber(capacity_val);

              if (v1 > 0) {
                demand_val = v1;
              } else {
                demand_val = demand_val.toFixed(2);
              }

              if (v2 > 0) {
                capacity_val = v2;
              } else {
                capacity_val = capacity_val.toFixed(2);
              }

              tooltip_html += `
                <div style='font-size: 12px; color: black; display: flex; flex-direction: column;'>
                  <div style='display: flex; align-items: center;'><p style='width: 80px'>Factory</p>: ${fact_data.name}</div>
                  <div style='display: flex; align-items: center;'><p style='width: 80px'>Demand</p>: ${demand_val} ${unit}</div>
                  <div style='display: flex; align-items: center;'><p style='width: 80px'>Capacity</p>: ${capacity_val} ${unit}</div>
                  <div style='display: flex; align-items: center; color: ${color}'><p style='width: 80px'>CU%</p>: ${fact_data.cu}%</div>
                  <button onclick="handleFactoryNavigation('Factory:${fact_data.code}')" style='background: none; padding: 0; width: 100%; text-align: right; color: dodgerblue; text-decoration: underline;'>View more</button>
                </div>
                <hr style='margin-top: 8px' />
              `;
            });

            return `
              <div style='font-size: 12px; color: black; display: flex; flex-direction: column;'>
                ${tooltip_html}
              </div>
            `;
          }
        }

        return params.name;
      },
    },
    geo: {
      map: "India",
      roam: false,
      zoom: 1.25,
      itemStyle: {
        borderColor: "white",
      },
      emphasis: {
        label: {
          show: true,
          fontSize: 8,
          color: "#000",
        },
      },
    },
    visualMap: [
      {
        left: "right",
        type: "piecewise",
        itemSymbol: "circle",
        bottom: 0,
        seriesIndex: 1,
        dimension: 3,
        calculable: true,
        pieces: [
          { max: 80, color: "#009A6E", label: "< 80% CU" },
          { min: 80, max: 89, color: "#E9A400", label: "80%<CU<Threshold " },
          { min: 89, color: "#FF0027", label: "> Threshold CU" },
        ],
      },
      {
        min: 0,
        max: 100,
        left: 0,
        type: "piecewise",
        bottom: 0,
        seriesIndex: 0,
        text:
          mapValueToggle === "Demand"
            ? ["High Demand", "Low Demand"]
            : ["High Growth", "Low Growth"],
        calculable: true,
        inRange: {
          color: ["#C0C0C0", "#FFE3A0", "#FFCC00", "#FE9900", "#FA6401"],
        },
        orient: "vertical", // Ensure a vertical layout
        align: "left",
      },
    ],
    series: [
      {
        tooltip: {
          trigger: "item",
          borderColor: "white",
          borderWidth: 0,
          position: function (point, params, dom, rect, size) {
            const x = point[0] + 10;
            const y = point[1];
            return [x, y];
          },
          formatter: (params) => {
            if (params.seriesType === "map") {
              const { name, value, data } = params;
              let color = "black";
              if (data.cu > 90) {
                color = "red";
              }

              if (!BRANCH_STATE_MAPPING[name]) {
                return name;
              }

              if (mapGranularity === "Branch") {
                let branchDetails = getBranchDetails(name,branchMasterData,CLUSTER_BRANCH_MAPPING);
                if (mapValueToggle === "Demand") {
                  let demand_val;

                  if (unitToggle === "MU") {
                    demand_val = data.demand_mu;
                  } else {
                    demand_val = data.demand / 1000;
                  }

                  let tmp = roundOffNumber(demand_val);
                  if (tmp > 0) {
                    demand_val = tmp;
                  } else {
                    try {
                      demand_val = demand_val.toFixed(2);
                    } catch (e) {
                      console.error(e);
                    }
                  }

                  let unit = unitToggle === "MU" ? "MU" : "KT";

                  return `
                    <div style='font-size: 12px; color: black; display: flex; flex-direction: column;'>
                      <div style='display: flex; align-items: center;'><p style='width: 80px'>Branch Code</p>: ${
                        branchDetails?.branchcode
                      }</div>
                      <div style='display: flex; align-items: center;'><p style='width: 80px'>Branch Name</p>: ${
                        branchDetails?.branchname
                      }</div>
                      <div style='display: flex; align-items: center;'><p style='width: 80px'>Demand</p>:
                      ${demand_val} ${unit}</div>
                    </div>
                  `;
                } else
                  return `
                    <div style='font-size: 12px; color: black; display: flex; flex-direction: column;'>
                      <div style='display: flex; align-items: center;'><p style='width: 80px'>Branch Code</p>: ${
                       branchDetails?.branchcode
                      }</div>
                      <div style='display: flex; align-items: center;'><p style='width: 80px'>Branch Name</p>: ${
                        branchDetails?.branchname
                      }</div>
                      <div style='display: flex; align-items: center;'><p style='width: 80px'>Growth</p>: ${
                        data.growth
                      }%</div>
                    </div>
                  `;
              } else {
                 let clusterDetails = getClusterDetails(name,branchMasterData,CLUSTER_BRANCH_MAPPING);
                if (mapValueToggle === "Demand") {
                  let demand_val;

                  if (unitToggle === "MU") {
                    demand_val = data.demand_mu;
                  } else {
                    demand_val = data.demand / 1000;
                  }

                  let tmp = roundOffNumber(demand_val);
                  if (tmp > 0) {
                    demand_val = tmp;
                  } else {
                    try {
                      demand_val = demand_val.toFixed(2);
                    } catch (e) {
                      console.error(e);
                    }
                  }

                  let unit = unitToggle === "MU" ? "MU" : "KT";

                  return `
                    <div style='font-size: 12px; color: black; display: flex; flex-direction: column;'>
                      <div style='display: flex; align-items: center;'><p style='width: 80px'>Cluster Code</p>: ${
                        name === "MAHARASHTRA"
                          ? maharashtraData.code
                          : clusterDetails?.clustercode
                      }</div>
                      <div style='display: flex; align-items: center;'><p style='width: 80px'>Cluster Name</p>: ${
                        name === "MAHARASHTRA"
                          ? maharashtraData.name
                          : clusterDetails?.clustername
                      }</div>
                      <div style='display: flex; align-items: center;'><p style='width: 80px'>Demand</p>: ${
                        name === "MAHARASHTRA"
                          ? maharashtraData.demand
                          : demand_val
                      } ${unit}</div>
                    </div>
                  `;
                } else
                  return `
                    <div style='font-size: 12px; color: black; display: flex; flex-direction: column;'>
                      <div style='display: flex; align-items: center;'><p style='width: 80px'>Cluster Code</p>: ${
                        name === "MAHARASHTRA"
                          ? maharashtraData.code
                          : clusterDetails?.clustercode
                      }</div>
                      <div style='display: flex; align-items: center;'><p style='width: 80px'>Cluster Name</p>: ${
                        name === "MAHARASHTRA"
                          ? maharashtraData.name
                          :  clusterDetails?.clustername
                      }</div>
                      <div style='display: flex; align-items: center;'><p style='width: 80px'>Growth</p>: ${
                        name === "MAHARASHTRA"
                          ? maharashtraData.growth
                          : data.growth
                      }%</div>
                    </div>
                  `;
              }
            }

            return params.name;
          },
        },
        name: "India",
        type: "map",
        map: "India",
        geoIndex: 0,
        itemStyle: {
          areaColor: "white",
          borderColor: "white",
        },
        emphasis: {
          label: {
            show: false,
            fontSize: 11,
            color: "#000000",
          },
          itemStyle: {
            areaColor: "white",
            borderColor: "white",
          },
        },
        data:
          mapValueToggle === "Demand" ? heatmapData.demand : heatmapData.growth,
      },
      {
        name: "Locations",
        type: "scatter",
        coordinateSystem: "geo",
        data: tableData.location.map((item) => ({
          name: item.name,
          code: item.code,
          count: item.count,
          pin: item.pin,
          value: [...item.value, item.size, item.cu],
          itemStyle: {
            color: item.color,
            opacity: 1,
            borderColor: "white",
            borderWidth: 1,
          },
          demand: item.demand,
          demand_mu: item.demand_mu,
          capacity: item.capacity,
          capacity_mu: item.capacity_mu,
          cu: item.cu,
        })),
        symbolSize: (value) => value[2],
        label: {
          show: false,
        },
        emphasis: {
          label: {
            show: true,
            formatter: "{b}",
            color: "#000",
            textStyle: {
              fontSize: 11,
            },
          },
        },
      },
    ],
  };

  const onEvents = {
    click: handleCircleClick,
  };

  function sortTable(col) {
    if (tableSortCol === col) {
      setTableSortOrd(!tableSortOrd);
    } else {
      setTableSortCol(col);
      setTableSortOrd(true);
    }
  }

  return (
    <Box p={4} gap={2} flexGrow={1} display="flex" flexDirection="column">
      <Stack direction="row" gap={2} alignItems="center">
        <Typography fontWeight="bold" fontSize={21}>
          Overview
        </Typography>
        <OverviewButton
          label="CU%"
          value={tableData.kpi.cu_const + "%"}
          colorFrom="#EC4887"
          colorTo="#B954A4"
          onClick={() => navigate(ltcpRoutes.CU)}
          flag={true}
        />
        <OverviewButton
          label="Demand"
          value={
            unitToggle === "MU" ? tableData.kpi.demand_mu : tableData.kpi.demand
          }
          unit={unitToggle === "MU" ? "MI Units" : "KT"}
          colorFrom="#855FC0"
          colorTo="#5346BA"
          onClick={() => navigate(ltcpRoutes.DEMAND)}
          flag={true}
        />
        <OverviewButton
          label="Capacity"
          value={
            unitToggle === "MU" ? tableData.kpi.cap_mu : tableData.kpi.cap_const
          }
          unit={unitToggle === "MU" ? "MI Units" : "KT"}
          colorFrom="#46C5F2"
          colorTo="#6692D9"
          onClick={() => navigate(ltcpRoutes.CAPACITY)}
          flag={true}
        />
        <Stack
          direction="row"
          bgcolor="#FFDEDF"
          justifyContent="space-between"
          alignItems="center"
          width={180}
          px={2}
        >
          <Typography fontSize={14}>No of Alerts</Typography>
          <Typography fontSize={30} fontWeight="bold">
            {alertCount}
          </Typography>
        </Stack>
      </Stack>
      <Paper>
        <Stack
          px={2}
          direction="row"
          bgcolor="white"
          borderRadius="3px"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Stack px={3} gap={2} py={1} direction="row">
            <Box display="flex" flexDirection="row" alignItems="center">
              <ButtonBase
                onClick={(e) => setFilterDropdown(e.currentTarget)}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <TuneIcon fontSize="small" />
                <Typography ml={1} fontSize={12}>
                  Filter
                </Typography>
                <KeyboardArrowDownIcon fontSize="small" />
              </ButtonBase>
              <Menu
                open={Boolean(filterDropdown)}
                anchorEl={filterDropdown}
                onClose={() => setFilterDropdown(null)}
              >
                <Stack p={1} gap={1} width={150}>
                  <Stack sx={{ width: "100%", alignItems: "stretch" }}>
                    <Typography fontSize={12}>BG</Typography>
                    <MultiSearchFilter
                      value={bgState}
                      setValue={setBgState}
                      options={currentFilters.bg}
                      filterName="bg"
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Stack>
                  <Stack sx={{ width: "100%", alignItems: "stretch" }}>
                    <Typography fontSize={12}>Sub-Category</Typography>
                    <MultiSearchFilter
                      value={subcategoryState}
                      setValue={setSubcategoryState}
                      options={currentFilters.subcategory}
                      filterName="subcategory"
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Stack>
                  <Stack sx={{ width: "100%", alignItems: "stretch" }}>
                    <Typography fontSize={12}>Branch</Typography>
                    <MultiSearchFilter
                      value={branchState}
                      setValue={setBranchState}
                      options={currentFilters.branch}
                      filterName="branch"
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Stack>
                  <Stack sx={{ width: "100%", alignItems: "stretch" }}>
                    <Typography fontSize={12}>Brand</Typography>
                    <MultiSearchFilter
                      value={brandState}
                      setValue={setBrandState}
                      options={currentFilters.brand}
                      filterName="brand"
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Stack>
                  <Stack sx={{ width: "100%", alignItems: "stretch" }}>
                    <Typography fontSize={12}>Format L2</Typography>
                    <MultiSearchFilter
                      value={formatState}
                      setValue={setFormatState}
                      options={currentFilters.format}
                      filterName="format"
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Stack>
                  <Stack sx={{ width: "100%", alignItems: "stretch" }}>
                    <Typography fontSize={12}>Factory</Typography>
                    <MultiSearchFilter
                      value={factoryState}
                      setValue={setFactoryState}
                      options={currentFilters.factory}
                      filterName="factory"
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  </Stack>
                  <Stack direction="row" justifyContent="end">
                    <Button variant="text" onClick={clearFilterHandler}>
                      Clear
                    </Button>
                  </Stack>
                </Stack>
              </Menu>
            </Box>
            <Box gap={1} display="flex" flexDirection="row" alignItems="center">
              <Typography fontSize={12}>Year</Typography>
              <MultiSearchFilter
                width={100}
                options={cuFilters.year}
                value={yearState}
                setValue={(v) => {
                  setLoad18Months(false);
                  setYearState(v);
                }}
                filterName="year"
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
              />
            </Box>
            <Box gap={1} display="flex" flexDirection="row" alignItems="center">
              <Typography fontSize={12}>Quarter</Typography>
              <MultiSearchFilter
                width={100}
                options={["Q1", "Q2", "Q3", "Q4"]}
                value={quarterState}
                setValue={(v) => {
                  setLoad18Months(false);
                  setQuarterState(v);
                }}
                disabled={timeGranularity === "MoM"}
                filterName="quarter"
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
              />
            </Box>
            <Box gap={1} display="flex" flexDirection="row" alignItems="center">
              <Typography fontSize={12}>Month</Typography>
              <MultiSearchFilter
                width={100}
                options={[
                  "Jan",
                  "Feb",
                  "Mar",
                  "Apr",
                  "May",
                  "Jun",
                  "Jul",
                  "Aug",
                  "Sep",
                  "Oct",
                  "Nov",
                  "Dec",
                ]}
                value={monthState}
                setValue={(v) => {
                  setLoad18Months(false);
                  setMonthState(v);
                }}
                disabled={timeGranularity === "QoQ"}
                filterName="month"
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
              />
            </Box>
          </Stack>
          <ToggleButtonGroup
            exclusive
            value={unitToggle}
            onChange={(_, v) => v !== null && setUnitToggle(v)}
          >
            <ToggleButton value="Tonnes">Tonnes</ToggleButton>
            <ToggleButton value="MU">MU</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>
      <Stack
        gap={2}
        flexGrow={1}
        direction="row"
        width="calc(100% - 32px)"
        maxWidth="calc(100% - 32px)"
        height="calc(100vh - 236px)"
        maxHeight="calc(100vh - 236px)"
      >
        <Paper
          sx={{
            p: 1,
            width: "35%",
            minWidth: "35%",
            maxWidth: "35%",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Stack gap={1} position="absolute" top={15} right={15} zIndex={999}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={mapGranularity}
                onChange={(_, v) => v !== null && setMapGranularity(v)}
              >
                <ToggleButton value="Branch">Branch</ToggleButton>
                <ToggleButton value="Cluster">Cluster</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={mapValueToggle}
                onChange={(_, v) => v !== null && setMapValueToggle(v)}
              >
                <ToggleButton value="Demand">Demand</ToggleButton>
                <ToggleButton value="Growth%">Growth%</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>
          <ReactECharts
            notMerge
            option={mapLoaded ? option : {}}
            onEvents={onEvents}
            style={{ height: "100%", width: "100%" }}
          />
        </Paper>
        <Stack gap={2} sx={{ width: "65%", minWidth: "65%", maxWidth: "65%" }}>
          <Paper
            sx={{
              p: 1,
              display: "flex",
              flexDirection: "column",
              height: "calc(50vh - 130px)",
              maxHeight: "calc(50vh - 130px)",
            }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Typography fontSize={16} fontWeight="bold">
                CU%
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={timeGranularity}
                onChange={(_, v) => {
                  if (v !== null) {
                    setTimeGranularity(v);
                    if (v === "MoM") {
                      setQuarterState("All");
                      setMonthState("All");
                    } else {
                      setQuarterState("All");
                      setMonthState("All");
                    }
                  }
                }}
              >
                <ToggleButton value="QoQ">QoQ</ToggleButton>
                <ToggleButton value="MoM">MoM</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <DemandCapacityGraph
              labelBg
              labels={cuData.date.map((item) => {
                if (timeGranularity === "MoM") {
                  let month = item.split("-");
                  month =
                    monthNames[Number.parseInt(month[1]) - 1] + "-" + month[0];

                  return month;
                } else {
                  return item;
                }
              })}
              demand={
                unitToggle === "MU"
                  ? cuData.demand_mu.map((item) => roundOffNumber(item))
                  : cuData.demand.map((item) => roundOffNumber(item))
              }
              capacity={
                unitToggle === "MU"
                  ? cuData.cap_mu.map((item) => roundOffNumber(item))
                  : cuData.pack_cap_tpm_new.map((item) => roundOffNumber(item))
              }
              cu={cuData.cu_const.map((item) => roundOffNumber(item))}
              threshold={
                productType !== "All"
                  ? thresholdLevel[productType]
                  : thresholdLevel[subcategoryState]
              }
              unit={unitToggle}
            />
          </Paper>
          <Paper
            sx={{
              p: 1,
              gap: 1,
              height: "calc(50vh - 130px)",
              maxHeight: "calc(50vh - 130px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              overflowY: "auto",
            }}
          >
            <Stack
              gap={2}
              direction="row"
              justifyContent="end"
              alignItems="center"
            >
              <Tooltip
                title={rowToggleState ? "Collapse rows" : "Expand rows"}
                placement="top"
              >
                <IconButton size="small" onClick={toggleAllRows}>
                  {rowToggleState ? (
                    <CloseFullscreenIcon fontSize="small" />
                  ) : (
                    <OpenInFullIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Download data" placement="top">
                <IconButton size="small" onClick={exportToExcel}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <ToggleButtonGroup
                exclusive
                value={tableLevel}
                onChange={(_, v) => v !== null && setTableLevel(v)}
              >
                <ToggleButton value="national">National</ToggleButton>
                <ToggleButton value="branch">Branch</ToggleButton>
                <ToggleButton value="factory">Factory</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <TableContainer>
              <Table
                ref={tableRef}
                size="small"
                stickyHeader
                sx={{ height: "100%", maxHeight: "100%", overflowY: "auto" }}
              >
                <TableHead>
                  <TableRow>
                    {tableLevel === "factory" && (
                      <TableCell
                        sx={{
                          bgcolor: "#354acd",
                          color: '#FFFFFF',
                          fontWeight: "bold",
                          textWrap: "nowrap",
                          textAlign: "center",
                        }}
                        onClick={() => sortTable("factory")}
                      >
                        Factory
                        <FilterListIcon sx={{ fontSize: 12 }} />
                        {tableSortCol === "factory" &&
                          (tableSortOrd ? (
                            <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                          ) : (
                            <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                          ))}
                      </TableCell>
                    )}
                    {tableLevel === "branch" && (
                      <TableCell
                        sx={{
                          bgcolor: "#354acd",
                          color: '#FFFFFF',
                          fontWeight: "bold",
                          textWrap: "nowrap",
                          textAlign: "center",
                        }}
                        onClick={() => sortTable("branch")}
                      >
                        Branch
                        <FilterListIcon sx={{ fontSize: 12 }} />
                        {tableSortCol === "branch" &&
                          (tableSortOrd ? (
                            <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                          ) : (
                            <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                          ))}
                      </TableCell>
                    )}
                    <TableCell
                      sx={{
                        bgcolor: "#354acd",
                        color: '#FFFFFF',
                        fontWeight: "bold",
                        textWrap: "nowrap",
                        textAlign: "center",
                      }}
                      onClick={() => sortTable("brand")}>
                      Brand
                      <FilterListIcon sx={{ fontSize: 12 }} />
                      {tableSortCol === "brand" &&
                        (tableSortOrd ? (
                          <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                        ) : (
                          <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                        ))}
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#354acd",
                        color: '#FFFFFF',
                        fontWeight: "bold",
                        textWrap: "nowrap",
                        textAlign: "center",
                      }}
                      onClick={() => sortTable("format")}
                    >
                      Format L2
                      <FilterListIcon sx={{ fontSize: 12 }} />
                      {tableSortCol === "format" &&
                        (tableSortOrd ? (
                          <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                        ) : (
                          <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                        ))}
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#354acd",
                        color: '#FFFFFF',
                        fontWeight: "bold",
                        textAlign: "center",
                        textWrap: "nowrap",
                      }}
                      onClick={() => sortTable("demand")}
                    >
                      Demand {unitToggle === "Tonnes" ? "(T)" : "(MU)"}
                      <FilterListIcon sx={{ fontSize: 12 }} />
                      {tableSortCol === "demand" &&
                        (tableSortOrd ? (
                          <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                        ) : (
                          <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                        ))}
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#354acd",
                        color: '#FFFFFF',
                        fontWeight: "bold",
                        textAlign: "center",
                        textWrap: "nowrap",
                      }}
                      onClick={() => sortTable("pack_cap_tpm_new")}
                    >
                      Capacity {unitToggle === "Tonnes" ? "(T)" : "(MU)"}
                      <FilterListIcon sx={{ fontSize: 12 }} />
                      {tableSortCol === "pack_cap_tpm_new" &&
                        (tableSortOrd ? (
                          <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                        ) : (
                          <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                        ))}
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#354acd",
                        color: '#FFFFFF',
                        fontWeight: "bold",
                        textAlign: "center",
                        textWrap: "nowrap",
                      }}
                      onClick={() => sortTable("cu_const")}
                    >
                      CU%
                      <FilterListIcon sx={{ fontSize: 12 }} />
                      {tableSortCol === "cu_const" &&
                        (tableSortOrd ? (
                          <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                        ) : (
                          <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                        ))}
                    </TableCell>
                     
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(tableSortOrd
                    ? tableData.data.sort((a, b) =>
                        typeof a[tableSortCol] === "string"
                          ? a[tableSortCol].localeCompare(b[tableSortCol])
                          : a[tableSortCol] - b[tableSortCol]
                      )
                    : tableData.data.sort((a, b) =>
                        typeof a[tableSortCol] === "string"
                          ? b[tableSortCol].localeCompare(a[tableSortCol])
                          : b[tableSortCol] - a[tableSortCol]
                      )
                  ).map((item, index) => (
                    <NestedTableRow
                      key={
                        bgState +
                        subcategoryState +
                        branchState +
                        brandState +
                        formatState +
                        factoryState +
                        yearState +
                        quarterState +
                        monthState +
                        unitToggle +
                        timeGranularity +
                        tableLevel +
                        index
                      }
                      data={item}
                      unitToggle={unitToggle}
                      rowIndex={index}
                      level={tableLevel}
                      bgState={bgState}
                      subcategoryState={subcategoryState}
                      branchState={branchState}
                      brandState={brandState}
                      formatState={formatState}
                      factoryState={factoryState}
                      yearState={yearState}
                      quarterState={quarterState}
                      monthState={monthFilterValueConvert(monthState)}
                      timeGranularity={timeGranularity}
                      tableLevel={tableLevel}
                      tableSortCol={tableSortCol}
                      tableSortOrd={tableSortOrd}
                      load_18_months={load_ym}
                      threshold = { productType !== "All"
                                ? thresholdLevel[productType]
                                : 90}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      </Stack>
    </Box>
  );
}

export default DashboardPage;
